import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { string } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Avatar, Modal } from 'antd';
import { Card } from '@cards/Card';
import IconButton from '@components/IconButton';
import memberNamesAndImagesMap from '@assets/mockCareTeamMembers/memberNamesAndImagesMap';
import {
  faUpRightAndDownLeftFromCenter,
  faDownLeftAndUpRightToCenter,
} from '@fortawesome/sharp-solid-svg-icons';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { fetchAppUsers } from '@redux/appUsers/actions';
import { selectAppUser } from '@redux/appUsers/reducers';
import {
  fetchAppUserCareTeam,
  createCareTeamRoleMember,
  createCareTeamRoleAndMember,
  editCareTeamRoleMember,
  deleteCareTeamRole,
  deleteCareTeamRoleMember,
} from '@redux/careTeam/actions';
import { selectAppUserCareTeam } from '@redux/careTeam/reducers';
import AddEditMemberModal from './AddEditMemberModal';
import './style.less';

const getInitials = name => {
  if (!name) return '';
  return name
    .split(' ')
    .map(word => word.charAt(0).toUpperCase())
    .join('');
};

const CareTeamMembersCard = ({ appUserId, ...props }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [loadingAppUser] = useSelector(selectAppUser(appUserId));
  const [loadingCareTeamRoles, careTeamRoles] = useSelector(selectAppUserCareTeam(appUserId));

  useEffect(() => {
    dispatch(fetchAppUsers());
    dispatch(fetchAppUserCareTeam(appUserId));
  }, [dispatch, appUserId]);

  // const hasCareTeamRoles = useMemo(() => {
  //   return careTeamRoles.length > 0;
  // }, [careTeamRoles]);

  const careTeamRoleMembers = useMemo(() => {
    if (!careTeamRoles || careTeamRoles.length === 0) {
      return [];
    }

    const entries = [];
    const seenEntryIds = new Set();

    careTeamRoles.forEach(role => {
      const members = role.members || [];

      if (members.length > 0) {
        members.forEach(member => {
          const entryId =
            member.id || `care-team-role-${role.id}-${member.staffUserId || 'member'}`;
          if (seenEntryIds.has(entryId)) {
            return;
          }
          seenEntryIds.add(entryId);

          entries.push({
            id: entryId,
            careTeamMemberId: member.id || null,
            roleId: role.id,
            roleName: role.name,
            roleDescription: role.description || '',
            name: member.name || '',
            email: member.email || '',
            phone: member.phone || '',
            businessName: member.businessName || '',
            createdByType: role.createdByType,
          });
        });
      } else {
        const entryId = `care-team-role-${role.id}`;
        if (seenEntryIds.has(entryId)) {
          return;
        }
        seenEntryIds.add(entryId);

        entries.push({
          id: entryId,
          careTeamMemberId: null,
          roleId: role.id,
          roleName: role.name,
          roleDescription: role.description || '',
          name: '',
          email: '',
          phone: '',
          businessName: '',
          createdByType: role.createdByType,
        });
      }
    });

    return entries;
  }, [careTeamRoles]);

  const careTeamMembers = careTeamRoleMembers;

  const loading = loadingAppUser || loadingCareTeamRoles;

  const displayedMembers = isExpanded ? careTeamMembers : careTeamMembers.slice(0, 5);

  const handleAddMember = () => {
    setSelectedMember(null);
    setModalVisible(true);
  };

  const handleEditMember = member => {
    setSelectedMember(member);
    setModalVisible(true);
  };

  const handleSaveMember = memberData => {
    const hasRoleId = !!selectedMember?.roleId;
    const hasCareTeamMemberId = !!selectedMember?.careTeamMemberId;
    const memberDetailsEmpty =
      !memberData.name && !memberData.email && !memberData.phone && !memberData.businessName;

    if (selectedMember) {
      if (hasRoleId && hasCareTeamMemberId && memberDetailsEmpty) {
        // Delete member when all member details are cleared
        dispatch(
          deleteCareTeamRoleMember(
            appUserId,
            selectedMember.roleId,
            selectedMember.careTeamMemberId,
          ),
        );
      } else if (hasRoleId && hasCareTeamMemberId) {
        // Edit existing member
        dispatch(
          editCareTeamRoleMember(
            appUserId,
            selectedMember.roleId,
            selectedMember.careTeamMemberId,
            {
              name: memberData.name,
              email: memberData.email,
              phone: memberData.phone,
              businessName: memberData.businessName,
            },
          ),
        );
      } else if (hasRoleId && !hasCareTeamMemberId && !memberDetailsEmpty) {
        // Add member to existing role that has no member
        dispatch(
          createCareTeamRoleMember(appUserId, selectedMember.roleId, {
            name: memberData.name,
            email: memberData.email,
            phone: memberData.phone,
            businessName: memberData.businessName,
          }),
        );
      }
    } else if (!memberDetailsEmpty) {
      // Create new role and member
      dispatch(
        createCareTeamRoleAndMember(appUserId, {
          name: memberData.roleName,
          description: memberData.roleDescription,
          members: [
            {
              name: memberData.name,
              email: memberData.email,
              phone: memberData.phone,
              businessName: memberData.businessName,
            },
          ],
        }),
      );
    }
    setModalVisible(false);
    setSelectedMember(null);
  };

  const handleDeleteMember = member => {
    setModalVisible(false);
    setMemberToDelete(member);
  };

  const confirmDeleteMember = () => {
    if (
      memberToDelete &&
      memberToDelete.createdByType !== 'pathway_assignment' &&
      memberToDelete.roleId
    ) {
      dispatch(deleteCareTeamRole(appUserId, memberToDelete.roleId));
    }
    setMemberToDelete(null);
  };

  const cancelDeleteMember = () => {
    setMemberToDelete(null);
  };

  return (
    <Card
      className="care-team-members-card"
      loading={loading}
      title={t('cards:CareTeamMembers.title', { count: careTeamMembers.length })}
      extra={[
        <IconButton
          key="add-care-team-member"
          icon={faPlus}
          tooltip={t('cards:CareTeamMembers.addTooltip')}
          onClick={handleAddMember}
        />,
        ...(careTeamMembers.length > 5
          ? [
              <IconButton
                key="expand-collapse"
                icon={isExpanded ? faDownLeftAndUpRightToCenter : faUpRightAndDownLeftFromCenter}
                tooltip={t(
                  isExpanded
                    ? 'cards:CareTeamMembers.compressTooltip'
                    : 'cards:CareTeamMembers.expandTooltip',
                )}
                onClick={() => {
                  setIsExpanded(!isExpanded);
                }}
              />,
            ]
          : []),
      ]}
      {...props}
    >
      <div className="care-team-members-grid">
        {displayedMembers.map(member => (
          <div
            key={member.id}
            className="care-team-member"
            onClick={() => handleEditMember(member)}
          >
            <Avatar
              size={64}
              icon={member.name && !memberNamesAndImagesMap[member.name] ? undefined : 'user'}
              src={memberNamesAndImagesMap[member.name] || undefined}
            >
              {member.name && !memberNamesAndImagesMap[member.name]
                ? getInitials(member.name)
                : null}
            </Avatar>
            {member.name && <p className="care-team-member-name">{member.name}</p>}
            {member.roleName && <p className="care-team-member-role">{member.roleName}</p>}
            {member.email && (
              <a
                href={`mailto:${member.email}`}
                className="care-team-member-email"
                onClick={e => e.stopPropagation()}
              >
                {member.email}
              </a>
            )}
            {member.phone && (
              <a
                href={`tel:${member.phone}`}
                className="care-team-member-phone"
                onClick={e => e.stopPropagation()}
              >
                {member.phone}
              </a>
            )}
          </div>
        ))}
      </div>
      <AddEditMemberModal
        visible={modalVisible}
        member={selectedMember}
        onSave={handleSaveMember}
        onDelete={handleDeleteMember}
        onCancel={() => {
          setModalVisible(false);
          setSelectedMember(null);
        }}
      />
      <Modal
        title={t('cards:CareTeamMembers.deleteConfirm.title')}
        visible={!!memberToDelete}
        onOk={confirmDeleteMember}
        onCancel={cancelDeleteMember}
        okText={t('cards:CareTeamMembers.deleteConfirm.ok')}
        cancelText={t('cards:CareTeamMembers.deleteConfirm.cancel')}
        okButtonProps={{ danger: true }}
      >
        <p>
          {t('cards:CareTeamMembers.deleteConfirm.message', {
            name: memberToDelete?.name || '',
          })}
        </p>
      </Modal>
    </Card>
  );
};

CareTeamMembersCard.propTypes = {
  appUserId: string.isRequired,
};

export default CareTeamMembersCard;
